/*
 * Round 14b (stats page redesign): what the Slot Stats tiles, charts and tables draw.
 *
 * Input is ALWAYS normaliseSlotStats()'s result (lib/slotHistory.js owns the server contract and
 * the parsing); nothing here reads a server field directly, so V3 draws only what the server sent.
 *
 * Conventions, the same in every function:
 *   - a day is 'YYYY-MM-DD'. `days` arrays are OLDEST FIRST (the way a chart reads left to right);
 *     table rows are NEWEST FIRST (the way Social Blade's daily table reads).
 *   - null means "not recorded". It is never summed as 0 and never drawn as a zero bar.
 *   - growth values are dated from the profile check's day (growthDay0), action values from the
 *     counters' day (actionsDay0) - the F4 pairing by DATE, as dailyTableRows does. Each kind of
 *     view ENDS on its own anchor (rangeDays 'growth' / 'actions'): in the morning, before the day's
 *     profile check, the counters are already on today and the growth is still on yesterday, and
 *     neither chart should end in an empty "not recorded" day.
 *   - counts (followers at each day's profile check) are rebuilt backwards from the check's exact
 *     count: count(day0) = lastChecked..Count, count(day i+1) = count(day i) - change(day i). ONLY
 *     from the profile check: the worker's header text ("15.8K") is rounded and is not the base the
 *     daily changes were measured from, so with it the charts show net change instead of counts.
 *   - the oldest growth entry of an untruncated CSV is the first check's seed (growth.seed): its
 *     COUNT is known (it is the first check's count) but its change is not recorded.
 *   - today's action count is a day in progress (partialDay): it is drawn lighter, and it is left
 *     out of every average and ratio.
 */
import { METRICS, shiftDay, daysBetween, isoDay } from './slotHistory';

export const RANGES = [7, 30, 90];
export const PROJECTION_HORIZONS = [7, 30, 90];
/** Fewest recorded days a projection is made from (a 7-day range needs its 7). */
export const PROJECTION_MIN_DAYS = 14;
/** A profile check older than this many days makes no projection (its dates would be in the past). */
export const PROJECTION_MAX_AGE = 2;
/** How far the dashed projection runs past the last check on the followers chart, per range. */
export const CHART_PROJECTION_DAYS = { 7: 3, 30: 10, 90: 30 };
/** The range from which the bar charts draw weekly bars (90 daily bars are 1-2px on a phone). */
export const WEEKLY_FROM_RANGE = 90;
/** The action types the actions-done ratio counts (a story view is not something the other account sees). */
export const RATIO_ACTIONS = ['likes', 'comments', 'follows', 'welcomeDms', 'storyLikes'];

const growthDay0 = (stats) => stats.growthDay0 || stats.actionsDay0;
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export const growthOf = (stats, key) => stats.growth.find((g) => g.key === key) || null;
export const metricOf = (stats, key) => stats.metrics.find((m) => m.key === key) || null;

/** The exact count at growth day 0, from the daily profile check only; else null. */
export function baseCount(stats, key) {
  const c = stats.current;
  if (!c || c.source !== 'profileCheck') return null;
  return isNum(c[key]) ? c[key] : null;
}

/**
 * One growth history as date-indexed tracks: { present, day0, changes[], counts[], seedDay }
 * (newest first, like the server's CSV). changes[] holds null for the seed entry; counts[] is null
 * when the base count is not known. `present` = at least one change was really recorded.
 */
export function growthTrack(stats, key) {
  const g = growthOf(stats, key);
  const day0 = growthDay0(stats);
  const raw = g && g.present ? g.series : [];
  const changes = raw.map((v, i) => (g.seed && i === raw.length - 1 ? null : v));
  const base = baseCount(stats, key);
  let counts = null;
  if (base !== null) {
    counts = [];
    let v = base;
    for (let i = 0; i < changes.length; i++) {
      counts.push(v);
      v -= changes[i] || 0;
    }
  }
  const seedDay = g && g.seed && raw.length ? shiftDay(day0, raw.length - 1) : null;
  return { key, present: changes.some((c) => c !== null), day0, changes, counts, base, seedDay };
}

const at = (arr, i) => (arr && i >= 0 && i < arr.length ? arr[i] : null);

/** The change recorded for `date`, or null. */
export function changeOn(track, date) {
  return track.present ? at(track.changes, daysBetween(track.day0, date)) : null;
}

/** The count at `date`'s profile check, or null (not recorded, or no exact base). */
export function countOn(track, date) {
  return track.counts ? at(track.counts, daysBetween(track.day0, date)) : null;
}

/** The value of an action metric on `date` (dated from the counters' day), or null. */
export function actionOn(stats, metric, date) {
  if (!metric || !metric.present) return null;
  return at(metric.series, daysBetween(stats.actionsDay0, date));
}

/** The day whose action counts are still running (the counters are on the viewer's today), else null. */
export function partialDay(stats) {
  return stats.actionsDay0 === stats.today ? stats.today : null;
}

const GROWTH_KEYS = ['followers', 'followings', 'posts'];
const tracksOf = (stats) => GROWTH_KEYS.map((k) => growthTrack(stats, k));
const hasGrowthData = (tracks, date) => tracks.some((t) => changeOn(t, date) !== null || countOn(t, date) !== null);
const hasActionData = (stats, date) => stats.metrics.some((m) => actionOn(stats, m, date) !== null);

/** The newest day that holds any data (the newer of the two anchors in use); null when none. */
export function newestDay(stats) {
  const anchors = [];
  if (tracksOf(stats).some((t) => t.present)) anchors.push(growthDay0(stats));
  if (stats.metrics.some((m) => m.present)) anchors.push(stats.actionsDay0);
  if (!anchors.length) return null;
  return anchors.reduce((a, b) => (daysBetween(a, b) >= 0 ? a : b));
}

/**
 * The calendar days a view draws for a range, OLDEST FIRST: the last `range` days ending on the
 * view's anchor - kind 'growth' ends on the profile check's day, 'actions' on the counters' day,
 * 'all' (the daily table) on the newer of the two - with the leading days that hold nothing of that
 * kind trimmed off (a 90-day range over 20 recorded days draws 20 days, not 70 empty ones). Days
 * INSIDE the span that hold nothing stay: a gap is information.
 */
export function rangeDays(stats, range, kind = 'all') {
  const tracks = tracksOf(stats);
  const anyGrowth = tracks.some((t) => t.present);
  const anyActions = stats.metrics.some((m) => m.present);
  let end;
  let has;
  if (kind === 'growth') {
    if (!anyGrowth) return [];
    end = growthDay0(stats);
    has = (d) => hasGrowthData(tracks, d);
  } else if (kind === 'actions') {
    if (!anyActions) return [];
    end = stats.actionsDay0;
    has = (d) => hasActionData(stats, d);
  } else {
    end = newestDay(stats);
    has = (d) => (anyGrowth && hasGrowthData(tracks, d)) || hasActionData(stats, d);
  }
  if (!end) return [];
  const newestFirst = [];
  for (let k = 0; k < range; k++) newestFirst.push(shiftDay(end, k));
  while (newestFirst.length && !has(newestFirst[newestFirst.length - 1])) newestFirst.pop();
  return newestFirst.reverse();
}

/**
 * The points a growth chart draws over `days`: { date, change, count, net } per day, where `net`
 * is the running change since the start of the range (what is drawn when no exact base exists).
 * mode: 'count' (exact counts known), 'net' (changes only), 'none' (no change recorded in range).
 */
export function growthPoints(stats, key, days) {
  const t = growthTrack(stats, key);
  let net = 0;
  let seen = false;
  const points = days.map((date) => {
    const change = changeOn(t, date);
    if (change !== null) { net += change; seen = true; }
    return { date, change, count: countOn(t, date), net: change === null ? null : net, seed: date === t.seedDay };
  });
  const recorded = points.filter((p) => p.change !== null).length;
  const mode = !seen ? 'none' : (t.counts ? 'count' : 'net');
  return { key, mode, points, recorded, track: t };
}

/**
 * A headline tile: the current value, the change over the range (absolute, % of the count at the
 * start of the range, per RECORDED day), the same change over the `range` days before, and a
 * sparkline.
 */
export function headline(stats, key, days, range = days.length) {
  const gp = growthPoints(stats, key, days);
  const withData = gp.points.filter((p) => p.change !== null);
  const change = withData.length ? withData.reduce((n, p) => n + p.change, 0) : null;
  const first = withData[0];
  const start = first && first.count !== null ? first.count - first.change : null;
  const pct = change !== null && start !== null && start > 0 ? (change / start) * 100 : null;
  const cur = stats.current;
  const value = cur && isNum(cur[key]) ? cur[key] : null;
  const text = cur && cur.text ? cur.text[key] : null;
  const spark = gp.mode === 'count'
    ? gp.points.filter((p) => p.count !== null).map((p) => p.count)
    : withData.map((p) => p.net);
  let previous = null;
  if (days.length && gp.track.present) {
    let sum = 0;
    let n = 0;
    for (let k = 1; k <= range; k++) {
      const c = changeOn(gp.track, shiftDay(days[0], k));
      if (c !== null) { sum += c; n += 1; }
    }
    if (n) previous = { change: sum, recordedDays: n, perDay: sum / n, days: range };
  }
  const approx = cur && cur.approx ? !!cur.approx[key] : !!(cur && cur.approximate);
  return {
    key,
    value,
    text: text === undefined ? null : text,
    approximate: approx,
    source: cur ? cur.source : null,
    change,
    pct,
    perDay: withData.length ? change / withData.length : null,
    recordedDays: withData.length,
    range,
    previous,
    spark,
  };
}

/**
 * Daily actions over `days`, OLDEST FIRST: { date, partial, <metricKey>: n|null, total: n|null }.
 * `total` sums the recorded metrics of that day and is null when none was recorded.
 */
export function actionRows(stats, days) {
  const present = stats.metrics.filter((m) => m.present);
  const partial = partialDay(stats);
  return days.map((date) => {
    const row = { date, partial: date === partial, total: null };
    present.forEach((m) => {
      const v = actionOn(stats, m, date);
      row[m.key] = v;
      if (v !== null) row.total = (row.total || 0) + v;
    });
    return row;
  });
}

/** The value a bar of the actions chart draws for `key` ('all' = the day's total). */
export const actionValue = (row, key) => {
  const v = key === 'all' ? row.total : row[key];
  return v === undefined ? null : v;
};

/**
 * Per action type over the range (and 'all'): the total (today so far included), the average per
 * COMPLETE recorded day (today's running count left out) and how many days that average covers.
 * Only the types with a recorded day in the range, in METRICS order.
 */
export function actionSummary(stats, rows) {
  const keys = ['all', ...stats.metrics.filter((m) => m.present && rows.some((r) => r[m.key] !== null && r[m.key] !== undefined)).map((m) => m.key)];
  return keys.map((key) => {
    const vals = rows.map((r) => ({ v: actionValue(r, key), partial: r.partial })).filter((x) => x.v !== null);
    const full = vals.filter((x) => !x.partial);
    const m = key === 'all' ? null : metricOf(stats, key);
    return {
      key,
      label: key === 'all' ? 'All actions' : m.label,
      total: vals.length ? vals.reduce((a, x) => a + x.v, 0) : null,
      recorded: vals.length,
      avg: full.length ? full.reduce((a, x) => a + x.v, 0) / full.length : null,
      avgDays: full.length,
      includesPartial: vals.some((x) => x.partial),
    };
  });
}

/**
 * The action types whose history STARTS inside the range, after the range's first recorded action
 * day (the round-14 types begin part-way through, so the daily total changes meaning there):
 * [{ date, keys, labels }] oldest first.
 */
export function lateStarts(stats, days) {
  const present = stats.metrics.filter((m) => m.present);
  const first = days.find((d) => present.some((m) => actionOn(stats, m, d) !== null));
  if (!first || !days.length) return [];
  const last = days[days.length - 1];
  const groups = {};
  present.forEach((m) => {
    const start = shiftDay(stats.actionsDay0, m.series.length - 1);
    if (daysBetween(start, first) > 0 && daysBetween(last, start) >= 0) {
      (groups[start] = groups[start] || []).push(m);
    }
  });
  return Object.keys(groups).sort().map((date) => ({
    date, keys: groups[date].map((m) => m.key), labels: groups[date].map((m) => m.label),
  }));
}

/**
 * The actions totals table: per metric the counters' day, the last 7 / 30 / 90 days (counted back
 * from the counters' day) and the average per COMPLETE recorded day over the selected range (a
 * running today is left out). `days` = how many days are recorded, so a window longer than the
 * history can say so.
 */
export function actionTotals(stats, range) {
  const partial = partialDay(stats) !== null;
  return stats.metrics.map((m) => {
    if (!m.present) return { key: m.key, label: m.label, present: false, sent: m.sent };
    const s = m.series;
    const sum = (n) => s.slice(0, n).reduce((a, b) => a + (b || 0), 0);
    const avgFrom = partial ? 1 : 0;
    const avgVals = s.slice(avgFrom, Math.min(range, s.length));
    return {
      key: m.key,
      label: m.label,
      present: true,
      sent: m.sent,
      day0: s[0],
      day0Partial: partial,
      d7: sum(7),
      d30: sum(30),
      d90: sum(90),
      days: s.length,
      avg: avgVals.length ? avgVals.reduce((a, b) => a + (b || 0), 0) / avgVals.length : null,
      avgDays: avgVals.length,
    };
  });
}

/**
 * The Social-Blade-style daily table over `days`: rows NEWEST FIRST
 *   { date, isToday, partial, followers:{count,change}, followings:{...}, posts:{...}, actions }
 * and a summary { change:{followers,followings,posts}, average:{...}, actions:{total,average,averageDays,partial},
 * recorded:{followers,...,actions} } where each sum / average covers only the recorded days, and the
 * actions average leaves out a running today.
 */
export function dailyTable(stats, days) {
  const tracks = Object.fromEntries(GROWTH_KEYS.map((k) => [k, growthTrack(stats, k)]));
  const acts = actionRows(stats, days);
  const rows = days.map((date, i) => {
    const row = { date, isToday: date === stats.today, partial: acts[i].partial, actions: acts[i].total };
    GROWTH_KEYS.forEach((k) => { row[k] = { count: countOn(tracks[k], date), change: changeOn(tracks[k], date) }; });
    return row;
  }).reverse();
  const sumOf = (vals) => {
    const rec = vals.filter((v) => v !== null);
    return { total: rec.length ? rec.reduce((a, b) => a + b, 0) : null, n: rec.length };
  };
  const summary = { change: {}, average: {}, recorded: {}, actions: {} };
  GROWTH_KEYS.forEach((k) => {
    const s = sumOf(rows.map((r) => r[k].change));
    summary.change[k] = s.total;
    summary.average[k] = s.n ? s.total / s.n : null;
    summary.recorded[k] = s.n;
  });
  const a = sumOf(rows.map((r) => r.actions));
  const full = sumOf(rows.filter((r) => !r.partial).map((r) => r.actions));
  summary.actions = {
    total: a.total,
    average: full.n ? full.total / full.n : null,
    averageDays: full.n,
    partial: rows.some((r) => r.partial && r.actions !== null),
  };
  summary.recorded.actions = a.n;
  return { rows, summary };
}

/**
 * The actions-done ratios over the selected range, paired by DATE: only the days with BOTH a
 * recorded follower change and a recorded action count (a running today left out) count.
 *   perFollow: followers gained per 100 follows; perAction: per 100 of RATIO_ACTIONS.
 * @returns Array<{ key, value: number|null, gained, done, days, reason: null|'noOverlap'|'noActions' }>
 */
export function rangeRatios(stats, days) {
  const followers = growthTrack(stats, 'followers');
  if (!followers.present) return [];
  const partial = partialDay(stats);
  const byKey = {};
  stats.metrics.forEach((m) => { if (m.present) byKey[m.key] = m; });
  const defs = [];
  if (byKey.follows) defs.push({ key: 'perFollow', keys: ['follows'] });
  const actionKeys = RATIO_ACTIONS.filter((k) => byKey[k]);
  if (actionKeys.length) defs.push({ key: 'perAction', keys: actionKeys });
  return defs.map((def) => {
    let gained = 0;
    let done = 0;
    let n = 0;
    days.forEach((d) => {
      if (d === partial) return;
      const c = changeOn(followers, d);
      const vals = def.keys.map((k) => actionOn(stats, byKey[k], d)).filter((v) => v !== null);
      if (c === null || !vals.length) return;
      gained += c;
      done += vals.reduce((x, y) => x + y, 0);
      n += 1;
    });
    const value = n && done > 0 ? Math.round((gained / done) * 1000) / 10 : null;
    return { key: def.key, keys: def.keys, value, gained, done, days: n, reason: n === 0 ? 'noOverlap' : (done > 0 ? null : 'noActions') };
  });
}

/**
 * The followers projection at the pace of the selected range: the average daily change over the
 * last `range` RECORDED days (counted back from the profile check) carried forward from that check.
 * An estimate; only from at least min(range, 14) recorded days, and only while the latest check is
 * at most PROJECTION_MAX_AGE days old (older data would put its "by" dates in the past).
 * @returns {{ok:false, reason:'noHistory'|'stale'|'tooFew', have?, need?, age?, day0?} |
 *           {ok:true, perDay, windowDays, base, day0, horizons:[{days, change, count, date}]}}
 */
export function projection(stats, range) {
  const t = growthTrack(stats, 'followers');
  if (!t.present) return { ok: false, reason: 'noHistory' };
  const age = daysBetween(stats.today, t.day0);
  if (age > PROJECTION_MAX_AGE) return { ok: false, reason: 'stale', age, day0: t.day0 };
  const window = t.changes.slice(0, range).filter((c) => c !== null);
  const need = Math.min(range, PROJECTION_MIN_DAYS);
  if (window.length < need) return { ok: false, reason: 'tooFew', have: window.length, need };
  const perDay = window.reduce((a, b) => a + b, 0) / window.length;
  return {
    ok: true,
    perDay,
    windowDays: window.length,
    base: t.base,
    day0: t.day0,
    horizons: PROJECTION_HORIZONS.map((h) => {
      const change = Math.round(perDay * h);
      return { days: h, change, count: t.base === null ? null : t.base + change, date: shiftDay(t.day0, -h) };
    }),
  };
}

/** The dashed projection's points after the last check: [{ date, proj }] for `days` more days (needs an exact base). */
export function projectionLine(p, days) {
  if (!p || !p.ok || p.base === null || !(days > 0)) return [];
  return Array.from({ length: days }, (_, i) => ({ date: shiftDay(p.day0, -(i + 1)), proj: p.base + p.perDay * (i + 1) }));
}

/* ------------------------------------------------------------------ weekly bars */

/** 7-day buckets over `days` (oldest first), the LAST bucket ending on the last day: [{ from, to, days }]. */
export function weekBuckets(days) {
  const out = [];
  for (let end = days.length; end > 0; end -= 7) {
    const slice = days.slice(Math.max(0, end - 7), end);
    out.unshift({ from: slice[0], to: slice[slice.length - 1], days: slice });
  }
  return out;
}

/**
 * The follower-change bars: one per day, or with `weekly` one per 7-day bucket (keyed on its last
 * day). { date, from, to, span, recorded, change: sum of the recorded changes | null, count: the
 * count at the bucket's last check | null }.
 */
export function changeBars(points, weekly) {
  if (!weekly) {
    return points.map((p) => ({
      date: p.date, from: p.date, to: p.date, span: 1, recorded: p.change === null ? 0 : 1, change: p.change, count: p.count, seed: p.seed,
    }));
  }
  const byDate = Object.fromEntries(points.map((p) => [p.date, p]));
  return weekBuckets(points.map((p) => p.date)).map((b) => {
    const ps = b.days.map((d) => byDate[d]);
    const rec = ps.filter((p) => p.change !== null);
    const withCount = ps.filter((p) => p.count !== null);
    return {
      date: b.to, from: b.from, to: b.to, span: b.days.length, recorded: rec.length,
      change: rec.length ? rec.reduce((a, p) => a + p.change, 0) : null,
      count: withCount.length ? withCount[withCount.length - 1].count : null,
    };
  });
}

/**
 * The action bars: one per day, or with `weekly` one per 7-day bucket (keyed on its last day).
 * { date, from, to, span, recorded, partial, running, value, parts:{<metric>: n|null} }. A bar is
 * `partial` (drawn faint) when it holds a running today (`running`) or, weekly, fewer than 7
 * recorded days.
 */
export function actionBars(rows, key, weekly, metricKeys) {
  if (!weekly) {
    return rows.map((r) => {
      const v = actionValue(r, key);
      const parts = Object.fromEntries(metricKeys.map((k) => [k, r[k] === undefined ? null : r[k]]));
      return {
        date: r.date, from: r.date, to: r.date, span: 1, recorded: v === null ? 0 : 1, partial: r.partial, running: r.partial, value: v, parts,
      };
    });
  }
  const byDate = Object.fromEntries(rows.map((r) => [r.date, r]));
  return weekBuckets(rows.map((r) => r.date)).map((b) => {
    const rs = b.days.map((d) => byDate[d]);
    const rec = rs.filter((r) => actionValue(r, key) !== null);
    const parts = Object.fromEntries(metricKeys.map((k) => {
      const vs = rs.map((r) => r[k]).filter((v) => v !== null && v !== undefined);
      return [k, vs.length ? vs.reduce((a, v) => a + v, 0) : null];
    }));
    const running = rs.some((r) => r.partial);
    return {
      date: b.to, from: b.from, to: b.to, span: b.days.length, recorded: rec.length,
      partial: running || rec.length < 7,
      running,
      value: rec.length ? rec.reduce((a, r) => a + actionValue(r, key), 0) : null,
      parts,
    };
  });
}

/**
 * The stacked "All actions" chart (owner, 2026-09-29: "Stacked, all 8 types"): one segment per action
 * type in ONE fixed order - `keys` in METRICS order, the first at the bottom of the stack - the same
 * order as the legend, the tooltip and the totals table. `hidden` types are left out of what is drawn
 * (the y axis rescales to what is left) but stay in the day's total, so the tooltip can list them.
 *
 *   bars: actionBars(rows, 'all', weekly, keys) - daily or weekly, a running today flagged there.
 * @returns {{ keys, visible, hidden, rows: Array<bar & { seg: {<key>: n|null}, shown: n|null, top: key|null }>,
 *             max: number, avg: number|null, avgDays: number }}
 *   seg[k]: the value drawn for k (null = hidden or not recorded); shown: the drawn stack's height
 *   (null when no visible type was recorded); total: every recorded type, hidden ones included;
 *   top: the topmost drawn non-zero segment (it carries the rounded data end); max: the tallest
 *   drawn stack; avg: the visible types' average per COMPLETE recorded day (a running today left
 *   out), daily bars only - a weekly axis is per week, so it carries no per-day average.
 */
export function actionStack(bars, keys, hidden = [], { weekly = false } = {}) {
  const hide = new Set(visibleHidden(keys, hidden));
  const visible = keys.filter((k) => !hide.has(k));
  const rows = bars.map((b) => {
    const seg = {};
    let shown = null;
    let total = null;
    keys.forEach((k) => {
      const v = b.parts ? b.parts[k] : null;
      const rec = v !== null && v !== undefined;
      if (rec) total = (total || 0) + v;
      seg[k] = rec && !hide.has(k) ? v : null;
      if (seg[k] !== null) shown = (shown || 0) + v;
    });
    const top = [...visible].reverse().find((k) => seg[k] > 0) || null;
    return { ...b, seg, shown, total, top };
  });
  const max = rows.reduce((m, r) => Math.max(m, r.shown || 0), 0);
  const perWeek = weekly || bars.some((b) => b.span > 1);
  const full = perWeek ? [] : rows.filter((r) => !r.partial && r.shown !== null);
  return {
    keys,
    visible,
    hidden: keys.filter((k) => hide.has(k)),
    rows,
    max,
    avg: full.length ? full.reduce((a, r) => a + r.shown, 0) / full.length : null,
    avgDays: full.length,
  };
}

/** The hidden types that apply to `keys`: unknown keys dropped, and never all of them (then none). */
export function visibleHidden(keys, hidden) {
  const h = keys.filter((k) => (hidden || []).includes(k));
  return h.length >= keys.length ? [] : h;
}

/**
 * A legend click: hide `key`, or show it again. At least one type always stays drawn - hiding the
 * last visible one is refused (the same array comes back).
 */
export function toggleHidden(hidden, key, keys) {
  const cur = visibleHidden(keys, hidden);
  if (cur.includes(key)) return cur.filter((k) => k !== key);
  if (!keys.includes(key)) return cur;
  const next = keys.filter((k) => cur.includes(k) || k === key);
  return next.length >= keys.length ? cur : next;
}

/**
 * The stretches of consecutive days with nothing recorded, for shading on a chart:
 * [{ from, to, days }] (oldest first). `valueOf(row)` returns the row's value or null.
 */
export function nullRuns(rows, valueOf) {
  const runs = [];
  let cur = null;
  rows.forEach((r) => {
    if (valueOf(r) === null || valueOf(r) === undefined) {
      if (cur) { cur.to = r.date; cur.days += 1; } else { cur = { from: r.date, to: r.date, days: 1 }; runs.push(cur); }
    } else {
      cur = null;
    }
  });
  return runs;
}

/* ------------------------------------------------------------------ scales and ticks */

const STEPS = [1, 2, 2.5, 5, 10];

/**
 * Round y ticks: { lo, hi, step, ticks } spanning [min, max] with about `count` ticks.
 * `integer` keeps the step >= 1 (counts cannot be fractional); `zero` forces 0 into the domain
 * (bars grow from a zero baseline). A count axis (not `zero`) keeps at least a tenth of a step of
 * air between the data and each end, so 32 -> 33 posts reads 31..34 and not a full-height cliff
 * between the top and bottom of the plot; it never goes below 0 for data that does not.
 */
export function niceScale(min, max, { count = 5, integer = true, zero = false } = {}) {
  let lo = Number.isFinite(min) ? min : 0;
  let hi = Number.isFinite(max) ? max : 0;
  if (zero) { lo = Math.min(0, lo); hi = Math.max(0, hi); }
  if (hi < lo) [lo, hi] = [hi, lo];
  if (hi === lo) {
    if (zero || lo === 0) hi = lo + (integer ? 4 : 1);
    else { const pad = Math.max(integer ? 2 : 0.5, Math.abs(lo) * 0.01); lo -= pad; hi += pad; }
  }
  const rough = (hi - lo) / Math.max(1, count - 1);
  const mag = 10 ** Math.floor(Math.log10(rough));
  // Whole-number data never gets a 2.5 step: 0, 2.5, 5 is not a count, and 13,750 / 14,250 ticks
  // cannot be abbreviated to one decimal of a K.
  const cands = (integer ? [1, 2, 5, 10] : STEPS).map((s) => s * mag);
  let step = cands.find((s) => s >= rough - 1e-9) || 10 * mag;
  if (integer) step = Math.max(1, Math.round(step));
  let niceLo = Math.floor(lo / step + 1e-9) * step;
  let niceHi = Math.ceil(hi / step - 1e-9) * step;
  if (!zero) {
    if (lo - niceLo < step * 0.1 && !(min >= 0 && niceLo - step < 0)) niceLo -= step;
    if (niceHi - hi < step * 0.1) niceHi += step;
  }
  const ticks = [];
  for (let v = niceLo; v <= niceHi + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return { lo: niceLo, hi: niceHi, step, ticks };
}

const decimalsOf = (x) => {
  const s = String(Math.round(x * 1e6) / 1e6);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
};

/**
 * Tick labels for a y axis whose ticks are `step` apart: millions as M with as many decimals as the
 * step needs (1.225M, at most three), thousands as K with at most one (103K, 14.2K), else the full
 * number with commas (15,750; 999,500) - neighbouring ticks always read differently.
 */
export function tickFormatter(step, maxAbs) {
  const unit = (div, suffix, maxDec) => {
    const dec = decimalsOf(step / div);
    if (dec > maxDec) return null;
    return (v) => (v === 0 ? '0' : `${(v / div).toFixed(dec).replace(/\.0+$/, '')}${suffix}`);
  };
  let f = null;
  if (maxAbs >= 1e6) f = unit(1e6, 'M', 3);
  else if (maxAbs >= 1e4 && step >= 100) f = unit(1e3, 'K', 1);
  return f || ((v) => fmtInt(v));
}

/** Tick steps for a daily axis (whole weeks once a day per label is too many) and for a weekly one. */
export const DAY_TICK_STEPS = [1, 2, 3, 7, 14, 21, 28, 35, 42];
export const WEEK_TICK_STEPS = [1, 2, 3, 4, 5, 6, 8, 10, 13];

/**
 * The x ticks for a date axis: at most `maxTicks` labels, evenly spaced and anchored on the NEWEST
 * day (the right edge always carries a date). `steps`: WEEK_TICK_STEPS for weekly bars.
 */
export function dateTicks(days, maxTicks, steps = DAY_TICK_STEPS) {
  if (!days.length) return [];
  const n = days.length;
  const limit = Math.max(2, maxTicks);
  const step = steps.find((s) => Math.ceil(n / s) <= limit) || Math.ceil(n / limit);
  const out = [];
  for (let i = n - 1; i >= 0; i -= step) out.push(days[i]);
  return out.reverse();
}

/* ------------------------------------------------------------------ formatting */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** '2026-09-28' -> 'short' "28 Sep", 'medium' "28 Sep 2026", 'long' "Mon 28 Sep 2026". Locale-free. */
export function fmtDay(iso, style = 'short') {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  const base = `${d} ${MONTHS[m - 1]}`;
  if (style === 'short') return base;
  if (style === 'medium') return `${base} ${y}`;
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd} ${base} ${y}`;
}

/** A span of days: "23-29 Sep 2026", "28 Sep - 4 Oct 2026", "28 Dec 2025 - 3 Jan 2026" (no year with withYear=false). */
export function fmtSpan(from, to, withYear = true) {
  if (!from || !to) return '-';
  if (from === to) return withYear ? fmtDay(from, 'medium') : fmtDay(from);
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2] = to.split('-').map(Number);
  const tail = withYear ? fmtDay(to, 'medium') : fmtDay(to);
  if (y1 === y2 && m1 === m2) return `${d1}–${tail}`;
  if (y1 === y2 || !withYear) return `${fmtDay(from)} – ${tail}`;
  return `${fmtDay(from, 'medium')} – ${tail}`;
}

/** The server's "2025-9-16, 23:30" (or "2026-09-28T04:10") -> "16 Sep 2025, 23:30"; raw text when unreadable. */
export function fmtServerDateTime(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const day = isoDay(raw);
  if (!day) return String(raw).trim();
  const time = String(raw).match(/[T ,]\s*(\d{1,2}):(\d{2})/);
  return time ? `${fmtDay(day, 'medium')}, ${time[1].padStart(2, '0')}:${time[2]}` : fmtDay(day, 'medium');
}

export const fmtInt = (n) => (isNum(n) ? Math.round(n).toLocaleString('en-US') : '-');

export function fmtSigned(n) {
  if (!isNum(n)) return '-';
  const r = Math.round(n);
  if (r === 0) return '0';
  return (r > 0 ? '+' : '-') + Math.abs(r).toLocaleString('en-US');
}

/** A per-day rate: one decimal under 100, whole numbers above; signed when `signed`. */
export function fmtRate(n, { signed = true } = {}) {
  if (!isNum(n)) return '-';
  const abs = Math.abs(n);
  const body = abs >= 100 ? Math.round(abs).toLocaleString('en-US') : abs.toFixed(1);
  if (!signed) return (n < 0 ? '-' : '') + body;
  if (Number(body.replace(/,/g, '')) === 0) return '0';
  return (n > 0 ? '+' : '-') + body;
}

export function fmtPct(p) {
  if (!isNum(p)) return null;
  const abs = Math.abs(p);
  const body = abs > 0 && abs < 0.1 ? abs.toFixed(2) : abs.toFixed(1);
  if (Number(body) === 0) return '0%';
  return `${p > 0 ? '+' : '-'}${body}%`;
}

/** 15760 -> "15.8K", 1234567 -> "1.2M", 950 -> "950". */
export function fmtCompact(n) {
  if (!isNum(n)) return '-';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (abs >= 1e4) return `${sign}${(abs / 1e3).toFixed(abs >= 1e5 ? 0 : 1).replace(/\.0$/, '')}K`;
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
}

/** "1 day" / "3 days". */
export const plural = (n, word) => `${fmtInt(n)} ${word}${n === 1 ? '' : 's'}`;

/** The action metrics in METRICS order ({ key, label }). */
export const ACTION_SERIES = METRICS.map((m) => ({ key: m.key, label: m.label }));
